import { useCallback, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { GridActionsCellItem, GridToolbarContainer, GridTreeDataGroupingCell } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import { ButtonUI, DataGridIdentifiers, DatagridPremiumUi, OrderArrowsUI } from '../Interface';
import { DependenceOption } from '../../config/taskList';
import { Box, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import useParam from '../../context/ParamContext';

const EditToolbar = (props) => {
  const {
    disabled,
    setSelectedParentValues,
    setRowSelectionModel,
    setIsImportDialogOpen,
    fromProject,
    apiRef,
    autocompleteData,
    setSelectedValues,
  } = props;

  const handleClick = () => {
    setRowSelectionModel([]);
    setSelectedValues({});
    setSelectedParentValues({});
  };

  const allRows = Array.from(apiRef.current.getRowModels().values());

  const markAllAsDone = () => {
    const statusList = autocompleteData?.taskListStatus?.statusItems ?? [];
    const doneStatus = statusList.find((s) => parseInt(s?.preparedness) === 100);
    const doneStatusId = doneStatus?.itemId ?? null;

    if (allRows.length === 0 || doneStatusId === null) return;

    const updatedRows = allRows.map((row) => ({
      ...row,
      preparedness: 100,
      statusId: doneStatusId,
    }));

    apiRef.current.updateRows(updatedRows);
  };

  return (
    <GridToolbarContainer>
      <div className="flex justify-between w-full">
        <ButtonUI
          disabled={disabled}
          text="Új gyökérelem"
          size="sm"
          color="blue-gray"
          variant="gradient"
          className="mb-1.5"
          onClick={handleClick}
        />
        <Box display="flex" gap="1rem">
          {fromProject && (
            <>
              <ButtonUI
                text="Összes készre állítása"
                size="sm"
                color="blue-gray"
                variant="gradient"
                className="mb-1.5"
                onClick={markAllAsDone}
                disabled={allRows.length === 0}
              />
              <ButtonUI
                text="Sablon importálás"
                size="sm"
                color="blue-gray"
                variant="gradient"
                className="mb-1.5"
                onClick={() => setIsImportDialogOpen(true)}
              />
            </>
          )}
        </Box>
      </div>
    </GridToolbarContainer>
  );
};

const TemplateDialogTable = ({
  autoCompleteOptions,
  rows,
  apiRef,
  setSelectedValues,
  setSelectedParentValues,
  disabled,
  fromProject,
  setIsImportDialogOpen,
  showAttachDialog,
  autocompleteData,
  recomputeParentPreparednessChain,
  setConfirmDialogData,
  onCloseConfirmDialog,
}) => {
  const { params } = useParam();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const handleEditClick = (row) => () => {
    setSelectedValues(row);
    setSelectedParentValues({});
    setRowSelectionModel([]);
  };

  const handleSelectParent = (row) => {
    setSelectedValues({});
    setSelectedParentValues(row);
  };

  const handleOrderChange = (id, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    const allRows = Array.from(apiRef.current.getRowModels().values());

    const currentRow = allRows.find((r) => r.id === id);
    if (!currentRow) return;

    let siblingRows = allRows.filter((r) => r.parentId === currentRow.parentId).sort((a, b) => a.visOrder - b.visOrder);

    siblingRows = siblingRows.map((item, idx) => ({
      ...item,
      visOrder: idx + 1,
    }));

    const maxOrder = siblingRows.length;
    const updatedSiblings = siblingRows.map((item) => {
      if (item.id === id) {
        if (newOrder < 1 || newOrder > maxOrder) return item;
        return { ...item, visOrder: newOrder };
      } else if (item.visOrder === newOrder) {
        return { ...item, visOrder: currentRow.visOrder };
      }
      return item;
    });

    const updatedAllRows = allRows.map((row) => {
      const updated = updatedSiblings.find((u) => u.id === row.id);
      return updated ? updated : row;
    });

    apiRef.current.updateRows(updatedAllRows);
  };

  const handleDelete = (id) => {
    const allRows = Array.from(apiRef.current.getRowModels().values());
    const parentId = allRows.find((r) => r.id === id)?.parentId ?? null;

    const collectDescendants = (pid) => {
      const directChildren = allRows.filter((row) => row.parentId === pid);
      return directChildren.flatMap((child) => [child, ...collectDescendants(child.id)]);
    };

    const toDeleteIds = [id, ...collectDescendants(id).map((r) => r.id)];

    const remaining = allRows.filter((r) => !toDeleteIds.includes(r.id));

    const resequencePatches = [];
    if (parentId) {
      const siblings = remaining.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);

      siblings.forEach((s, idx) => {
        const want = idx + 1;
        if (s.visOrder !== want) {
          resequencePatches.push({ id: s.id, visOrder: want });
          s.visOrder = want;
        }
      });
    }

    const parentPrepPatches = parentId ? recomputeParentPreparednessChain(remaining, parentId) : [];

    apiRef.current.updateRows([
      ...toDeleteIds.map((rid) => ({ id: rid, _action: 'delete' })),
      ...resequencePatches,
      ...parentPrepPatches,
    ]);
    onCloseConfirmDialog();
    setSelectedValues({});
  };

  const handleCopy = (row) => {
    const rows = Array.from(apiRef.current.getRowModels().values());
    if (!row) return;

    const children = (id) => rows.filter((r) => r.parentId === id).flatMap((c) => [c, ...children(c.id)]);
    const subtree = [row, ...children(row.id)];

    const idMap = new Map(subtree.map((n) => [n.id, uuidv4()]));

    const parentId = row.parentId ?? null;
    const parentRow = parentId ? rows.find((r) => r.id === parentId) : null;
    const siblings = rows.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);
    const insertOrder = (row.visOrder ?? siblings.length) + 1;
    const shiftedSiblings = siblings.map((s) => (s.visOrder >= insertOrder ? { ...s, visOrder: s.visOrder + 1 } : s));

    const shiftDays = fromProject
      ? dayjs
          .max(subtree.map((n) => dayjs(n.startDate).add((n.duration ?? 1) - 1, 'day')))
          .add(1, 'day')
          .diff(dayjs(row.startDate), 'day')
      : 0;

    const clone = (n) => {
      const newId = idMap.get(n.id);
      const originalPath = n.path ?? [];
      let newPath;

      if (n.id === row.id) {
        const parentPath = parentRow?.path ?? [];
        newPath = parentId ? [...parentPath, newId] : [newId];
      } else {
        newPath = originalPath.map((pid) => idMap.get(pid) ?? pid);
      }

      return {
        ...n,
        id: newId,
        parentId: n.id === row.id ? parentId : n.parentId ? idMap.get(n.parentId) : null,
        path: newPath,
        visOrder: n.id === row.id ? insertOrder : n.visOrder,
        startDate:
          fromProject && n.startDate
            ? dayjs(n.startDate).add(shiftDays, 'day').format('YYYY-MM-DD') + 'T00:00:00.000Z'
            : n.startDate,
        isNew: true,
      };
    };

    apiRef.current.updateRows([
      ...rows.filter((r) => r.parentId !== parentId),
      ...shiftedSiblings,
      ...subtree.map(clone),
    ]);

    setSelectedValues({});
  };

  const columns = () => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        renderCell: (params) => {
          return <GridTreeDataGroupingCell {...params} label="" />;
        },
      },
      ...(fromProject
        ? [
            {
              field: 'startDate',
              headerName: 'Kezdő dátum',
              type: 'date',
              valueGetter: (_data, row) => {
                return row.startDate ? new Date(row.startDate) : '';
              },
            },
          ]
        : []),
      {
        field: 'duration',
        headerName: 'Időintervallum (nap)',
        type: 'number',
      },
      {
        field: 'waitingTime',
        headerName: 'Várakozási idő (nap)',
        type: 'number',
      },
      ...(fromProject
        ? [
            {
              field: 'statusId',
              headerName: 'Státusz',
              type: 'string',
              renderCell: (data) => {
                const findedStatus = autoCompleteOptions?.taskListStatus?.find(
                  (item) => item.value === data.row?.statusId
                );
                return findedStatus?.label;
              },
            },
          ]
        : []),
      {
        field: 'responsiblePersonId',
        headerName: 'Felelős',
        type: 'string',
        renderCell: (params) => {
          /*const findedRespPerson = autoCompleteOptions?.user?.find((item) => item.value === data?.responsiblePersonId);
          return findedRespPerson?.label;*/
          const data = params.row;
          const relevantValue =
            data.responsiblePersonId ??
            data.responsibleUserGroupId ??
            data.responsibleCompanyId ??
            data.responsibleWorkerId;

          const option = autoCompleteOptions?.responsible?.find(
            (item) => item.value === relevantValue && item.type === data?.responsibleType
          );
          // van egy ikon is az autocompleteOptions.responsible-ben

          return (
            <div>
              {option?.icon ? <span style={{ display: 'inline-flex', marginRight: 8 }}>{option.icon}</span> : null}
              {option?.label}
            </div>
          );
        },
      },
      {
        field: 'userId',
        headerName: 'Tulajdonos',
        type: 'string',
        renderCell: (data) => {
          const findedUser = autoCompleteOptions?.user?.find((item) => item.value === data.row?.userId);
          return findedUser?.label;
        },
      },
      {
        field: 'userGroupId',
        headerName: 'Felelősök (csoport)',
        type: 'string',
        renderCell: (data) => {
          const findedRespPerson = autoCompleteOptions?.userGroups?.find(
            (item) => item.value === data.row?.userGroupId
          );
          return findedRespPerson?.label;
        },
      },
      {
        field: 'dependence',
        headerName: 'Függőség',
        type: 'string',
        renderCell: (data) => {
          const foundedDep = DependenceOption?.find((item) => item.value === data.row?.dependence);
          return foundedDep?.label;
        },
      },
      ...(fromProject
        ? [
            {
              field: 'typeId',
              headerName: 'Típus',
              type: 'string',
              renderCell: (data) => {
                const findedType = autoCompleteOptions?.taskListType?.find((item) => item.value === data.row?.typeId);
                return findedType?.label;
              },
            },
          ]
        : []),
      ...(fromProject
        ? [
            {
              field: 'preparedness',
              headerName: 'Készültség',
              valueGetter: (_, params) => {
                return params?.preparedness ? `${params?.preparedness ?? 0} %` : '';
              },
            },
          ]
        : []),
      ...(fromProject
        ? [
            {
              field: 'description',
              headerName: 'Megjegyzés',
              type: 'string',
            },
          ]
        : []),
      ...(fromProject
        ? [
            {
              field: 'taskListAttachment',
              headerName: 'Csatolmányok',
              type: 'string',
              renderCell: (data) => {
                return data.row.isNew ? (
                  ''
                ) : (
                  <ButtonUI
                    size="sm"
                    className="bg-success px-2.5 py-1"
                    onClick={() => showAttachDialog(data?.row)}
                    text={
                      <Box display="flex">
                        <AttachFileIcon fontSize="small" />
                        <Typography fontSize="medium">{data?.row?.taskListAttachment?.length}</Typography>
                      </Box>
                    }
                  />
                );
              },
            },
          ]
        : []),
      ...(fromProject
        ? [
            {
              field: 'deadLine',
              headerName: 'Határidő',
              type: 'dateTime',
              valueGetter: (_data, row) => {
                return row.deadLine ? new Date(row.deadLine) : null;
              },
            },
          ]
        : []),
      {
        field: 'visOrder',
        type: 'string',
        sortable: true,
        headerName: 'Sorrend',
        renderCell: (params) => {
          const disabled = params?.api?.getSortModel()[0]?.field !== 'visOrder';
          const isSortedDescending = params.api.getSortModel()[0]?.sort === 'desc';

          const handleUpClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.id, params.row.visOrder, 'down');
            } else {
              handleOrderChange(params.row.id, params.row.visOrder, 'up');
            }
          };

          const handleDownClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.id, params.row.visOrder, 'up');
            } else {
              handleOrderChange(params.row.id, params.row.visOrder, 'down');
            }
          };

          return <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />;
        },
      },
      {
        field: 'construction',
        type: 'boolean',
        sortable: true,
        headerName: 'Kivitelezés',
      },
      ...(params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y'
        ? [
            {
              field: 'taskRatio',
              headerName: 'Feladat arány',
              type: 'number',
              sortable: false,
            },
          ]
        : []),
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: (params) => {
          const row = params.row;

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={handleEditClick(row)}
              color="inherit"
              disabled={row.isVirtual}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              color="inherit"
              onClick={() =>
                setConfirmDialogData({
                  open: true,
                  message: `Biztosan kitörli a(z) ${row.name} feladatot?`,
                  handleChangeElement: () => handleDelete(row.id),
                })
              }
              disabled={row.isVirtual}
            />,
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label="Copy"
              color="inherit"
              onClick={() => handleCopy(row)}
              disabled={params.row.isVirtual}
            />,
          ];
        },
      },
    ]);
  };

  const getTreeDataPath = useCallback((row) => row.path, []);

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <DatagridPremiumUi
            apiRef={apiRef}
            datagridIdentifier={DataGridIdentifiers.taskListDialog}
            getRowHeight={() => 'auto'}
            headerFilters={false}
            columns={columns()}
            slots={{
              toolbar: EditToolbar,
            }}
            slotProps={{
              toolbar: {
                disabled,
                setSelectedParentValues,
                setRowSelectionModel,
                setIsImportDialogOpen,
                fromProject,
                apiRef,
                autocompleteData,
                setSelectedValues,
              },
            }}
            rows={rows ?? []}
            treeData
            groupingColDef={{
              headerName: '',
              renderCell: () => null,
              width: 1,
              hideable: false,
              sortable: false,
              resizable: false,
            }}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            getTreeDataPath={getTreeDataPath}
            onRowClick={(rowData) => handleSelectParent(rowData.row)}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateDialogTable;
